import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from "react-router-dom";
import { SearchSongs } from './Elements.js'
import { useRadio } from '../helper/radioContext.js';
import { CHANNELS, STREAMS, getPlayingEntry } from '../helper/restapi.js';

const AudioPlayer = ({ audioUrl, isPlaying, toggleIsPlaying, currentPlaying, onAudioEnd }) => {
    const audioRef = useRef(null);
    const [active, setActive] = useState(false);
    const [hidePlayer, setHidePlayer] = useState(false);
    const [audioDuration, setAudioDuration] = useState(null);
    const [currentPosition, setCurrentPosition] = useState(null);
    const [currentPositionLive, setCurrentPositionLive] = useState(null);
    const [isHovered, setIsHovered] = useState(false);
    const liveRef = useRef(null);
    const artistRef = useRef(null);
    const trackRef = useRef(null);
    const [addExtraSpan, setAddExtraSpan] = useState(false);

    const { pathname } = useLocation();
    const { activeChannel, channelData, playChannel } = useRadio();
    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerRef = useRef(null);

    if (typeof currentPlaying !== 'object') {
        currentPlaying = JSON.parse(currentPlaying);
    }

    // Live streams (Hauptprogramm + Digitalradios) have no "info"; Radiothek items do
    const isLive = !currentPlaying.info;

    const liveChannel = isLive && CHANNELS[activeChannel] ? activeChannel : null;

    // Wie im Start-Player: Klick auf einen Stream in der Liste zeigt ihn nur an, erst der Play-Knopf spielt ihn ab.
    // viewChannel = angezeigter, aber (noch) nicht spielender Stream; folgt wieder dem laufenden, sobald dieser wechselt.
    const [viewChannel, setViewChannel] = useState(null);
    useEffect(() => {
        setViewChannel(null);
    }, [liveChannel, currentPlaying.info]);

    const viewing = viewChannel && viewChannel !== liveChannel && CHANNELS[viewChannel] ? viewChannel : null;
    const viewEntry = viewing ? getPlayingEntry(channelData[viewing]) : null;
    const shown = viewing
        ? { ...(viewEntry || { artist: '', title: '', cover: '' }), channelName: CHANNELS[viewing].name }
        : currentPlaying;
    const shownIsPlaying = viewing ? false : isPlaying;
    const shownChannel = viewing || liveChannel;

    // "Stream wechseln": Liste klappt nach oben auf, schließt bei Klick außerhalb, Escape,
    // Seitenwechsel oder wenn der Player (Startseite oben) ausgeblendet wird
    useEffect(() => {
        if (!pickerOpen) return;
        const onPointerDown = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setPickerOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setPickerOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [pickerOpen]);

    useEffect(() => {
        setPickerOpen(false);
    }, [pathname, hidePlayer]);

    // Zeile anklicken: Läuft gerade etwas, wird direkt auf den Stream gewechselt.
    // Ist der Player gestoppt, wird der Stream nur ausgewählt (angezeigt) – Play startet ihn.
    const viewFromList = (ch) => {
        if (isPlaying && ch !== liveChannel) {
            playChannel(ch);
        } else {
            setViewChannel(ch === liveChannel ? null : ch);
        }
        setPickerOpen(false);
    };

    // Play-Knopf in der Zeile = direkt abspielen bzw. pausieren
    const playFromList = (event, ch) => {
        event.stopPropagation();
        playChannel(ch);
        if (ch !== liveChannel) {
            setPickerOpen(false);
        }
    };

    const togglePlayPause = () => {
        // Angezeigter, nicht laufender Stream: Play startet ihn
        if (viewing) {
            playChannel(viewing);
            return;
        }
        if (isPlaying) {
            audioRef.current.pause();
            if (isLive) {
                audioRef.current.src = '';
                audioRef.current.currentTime = 0;
            }
        } else {
            if (isLive) {
                audioRef.current.src = audioUrl;
                audioRef.current.currentTime = 0;
            }
            audioRef.current.play();
        }
        toggleIsPlaying();
    };


    const handleScroll = () => {
        const scrollY = window.scrollY;
        if (scrollY > 150) {
            setHidePlayer(false);
        } else {
            setHidePlayer(true);
        }
    };

    useEffect(() => {
        const checkWidths = () => {
            if (liveRef.current && artistRef.current && trackRef.current) {
                const liveWidth = liveRef.current.getBoundingClientRect().width;
                const artistWidth = artistRef.current.getBoundingClientRect().width;
                const titleWidth = trackRef.current.getBoundingClientRect().width;

                setAddExtraSpan(liveWidth < (artistWidth + titleWidth));
            }
        };

        // Check on mount and resize
        checkWidths();
        window.addEventListener("resize", checkWidths);
        return () => {
            window.removeEventListener("resize", checkWidths);
        };
    }, []);

    useEffect(() => {
        if (pathname === '/') {
            setHidePlayer(true);
            window.addEventListener('scroll', handleScroll);

            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        } else {
            setHidePlayer(false);
        }
    }, [pathname]);

    useEffect(() => {
        if (isPlaying) {
            // src may have been cleared by a live pause in this bar – restore it before playing
            if (audioRef.current.src !== audioUrl) {
                audioRef.current.src = audioUrl;
            }
            audioRef.current.play();
        } else {
            audioRef.current.pause();
        }
    }, [isPlaying]);

    useEffect(() => {
        const onDurationChange = () => {
            setAudioDuration(audioRef.current.duration);
        };

        if (audioRef.current) {
            audioRef.current.addEventListener('durationchange', onDurationChange);

            return () => {
                audioRef.current.removeEventListener('durationchange', onDurationChange);
            };
        }
    }, [audioUrl]);


    const switchActive = () => {
        if (active) {
            setActive(false)
        } else {
            setActive(true)
        }
    };

    const handlePlaybackEnd = () => {
        if (currentPlaying && currentPlaying.info) {
            if (typeof onAudioEnd === 'function') {
                onAudioEnd();
            }
        }
    };

    const handleTimeUpdate = () => {
        setCurrentPosition(audioRef.current.currentTime);
    };

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.addEventListener('ended', handlePlaybackEnd);
            audioRef.current.addEventListener('timeupdate', handleTimeUpdate);

            return () => {
                audioRef.current.removeEventListener('ended', handlePlaybackEnd);
                audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
            };
        }
    }, [currentPlaying.info]);

    const jumpToPosition = (position) => {
        if (audioRef.current) {
            audioRef.current.currentTime = position;
        }
    };

    const handleProgressBarClick = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const width = rect.width;
        const percentClicked = offsetX / width;
        const newPosition = percentClicked * audioDuration;
        if (audioRef.current) {
            audioRef.current.currentTime = newPosition;
        }
    };

    useEffect(() => {
        if (!shown.info) {
            const calculateCurrentPosition = () => {
                const startTime = new Date(shown.start).getTime();
                var currentTime = new Date().getTime();

                currentTime = currentTime - 10 * 1000;

                const elapsedSeconds = (currentTime - startTime) / 1000;
                setCurrentPositionLive(elapsedSeconds / shown.length)
                
            };

            calculateCurrentPosition();
            const intervalId = setInterval(calculateCurrentPosition, 1000);

            return () => clearInterval(intervalId);
        }
    }, [shown.info, shown.start, shown.length]);

    return (
        <div id="player" className={`sticky-bottom ${hidePlayer ? 'hidden' : ''} ${active ? 'active' : ''}`}>
            <audio
                ref={audioRef}
                src={audioUrl}
                preload="none"
            />
            <div className="container stream-picker-wrap" ref={pickerRef}>
                <button
                    type="button"
                    className={`stream-switch ${pickerOpen ? 'open' : ''}`}
                    onClick={() => setPickerOpen(!pickerOpen)}
                    aria-expanded={pickerOpen}
                    aria-controls="stream-picker"
                >
                    Stream wechseln
                </button>
                { pickerOpen &&
                    <div className="stream-picker" id="stream-picker">
                        <div className="stream-picker-hl">{STREAMS.headline}</div>
                        { Object.keys(CHANNELS).map((ch) => {
                            const entry = getPlayingEntry(channelData[ch]);
                            const channelIsPlaying = isPlaying && liveChannel === ch;
                            return (
                                <div
                                    key={ch}
                                    role="button"
                                    tabIndex={0}
                                    className={`stream-option ${shownChannel === ch ? 'active' : ''}`}
                                    onClick={() => viewFromList(ch)}
                                    onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); viewFromList(ch); } }}
                                    aria-label={`${CHANNELS[ch].name} anzeigen`}
                                >
                                    <img className="stream-option-icon" src={CHANNELS[ch].icon} alt="" />
                                    <span className="stream-option-info">
                                        <span className="stream-option-name">{CHANNELS[ch].name}</span>
                                        <span className="stream-option-song">
                                            {entry ? <><b>{entry.artist}</b> – {entry.title}</> : ''}
                                        </span>
                                    </span>
                                    <button
                                        type="button"
                                        className="stream-option-play"
                                        onClick={(event) => playFromList(event, ch)}
                                        title={`${CHANNELS[ch].name} ${channelIsPlaying ? 'pausieren' : 'abspielen'}`}
                                    >
                                        <img src={channelIsPlaying ? '/icon/btn-pause.svg' : '/icon/btn-play.svg'} alt={`${CHANNELS[ch].name} ${channelIsPlaying ? 'pausieren' : 'abspielen'}`} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                }
                <div className="row align-items-center">
                    <div className="col-10 col-lg-6 col-xl-5">
                        <div  onClick={togglePlayPause} className="play d-inline-block">
                            {!shownIsPlaying &&
                                <img src="/icon/btn-play.svg" />
                            }
                            {shownIsPlaying &&
                                <img src="/icon/btn-pause.svg" />
                            }
                        </div>
                        <img className="cover" src={shown.cover} />
                        <div className="live d-inline-block" ref={liveRef}>
                            {!shown.info &&
                                <div className="hl">
                                    {shown.channelName ? shown.channelName : 'Jetzt live'}
                                    <svg xmlns="http://www.w3.org/2000/svg" width="25.071" height="21.575" viewBox="0 0 25.071 21.575"><g id="Gruppe_109" data-name="Gruppe 109" transform="translate(-867 -4513)"><line id="Linie_31" data-name="Linie 31" y2="10.889" transform="translate(868.5 4518.343)" fill="none" stroke="#3cad6c" strokeLinecap="round" strokeWidth="3"/><line id="Linie_33" data-name="Linie 33" y2="10.889" transform="translate(879.535 4518.343)" fill="none" stroke="#3cad6c" strokeLinecap="round" strokeWidth="3"/><line id="Linie_34" data-name="Linie 34" y2="10.889" transform="translate(890.571 4518.343)" fill="none" stroke="#3cad6c" strokeLinecap="round" strokeWidth="3"/><line id="Linie_32" data-name="Linie 32" y2="18.575" transform="translate(874.018 4514.5)" fill="none" stroke="#3cad6c" strokeLinecap="round" strokeWidth="3"/><line id="Linie_35" data-name="Linie 35" y2="18.575" transform="translate(885.053 4514.5)" fill="none" stroke="#3cad6c" strokeLinecap="round" strokeWidth="3"/></g></svg>
                                </div>
                            }
                            {shown.info &&
                                <div className="hl">
                                    Du hörst
                                    <svg xmlns="http://www.w3.org/2000/svg" width="25.071" height="21.575" viewBox="0 0 25.071 21.575"><g id="Gruppe_109" data-name="Gruppe 109" transform="translate(-867 -4513)"><line id="Linie_31" data-name="Linie 31" y2="10.889" transform="translate(868.5 4518.343)" fill="none" stroke="#3cad6c" strokeLinecap="round" strokeWidth="3"/><line id="Linie_33" data-name="Linie 33" y2="10.889" transform="translate(879.535 4518.343)" fill="none" stroke="#3cad6c" strokeLinecap="round" strokeWidth="3"/><line id="Linie_34" data-name="Linie 34" y2="10.889" transform="translate(890.571 4518.343)" fill="none" stroke="#3cad6c" strokeLinecap="round" strokeWidth="3"/><line id="Linie_32" data-name="Linie 32" y2="18.575" transform="translate(874.018 4514.5)" fill="none" stroke="#3cad6c" strokeLinecap="round" strokeWidth="3"/><line id="Linie_35" data-name="Linie 35" y2="18.575" transform="translate(885.053 4514.5)" fill="none" stroke="#3cad6c" strokeLinecap="round" strokeWidth="3"/></g></svg>
                                    { shown.info &&
                                        <span className="btn" onClick={handlePlaybackEnd}>Zum Live Radio</span>
                                    }   
                                </div>
                            }
                            { shown.info &&
                                <span className="btn btn-live d-lg-none" onClick={handlePlaybackEnd}>Zum Live Radio</span>
                            } 
                            <div
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                                
                            >
                                {isHovered ? (
                                    <marquee duplicated="true" className="artist-track">
                                        <span className="track">{shown.artist}</span>
                                        <span className="artist"><span className="sep"> - </span>{shown.title}</span>
                                    </marquee>
                                ) : (
                                    <div className="artist-track">
                                        <span className="track" ref={trackRef}>{shown.artist}</span>
                                        <span className="artist" ref={artistRef}><span className="sep"> - </span>{shown.title}</span>
                                        {addExtraSpan && <span className="extra"></span>}
                                    </div>
                                )}
                            </div>

                            { shown.info && audioDuration !== null &&
                                <span
                                    className="progress-bar"
                                    onClick={handleProgressBarClick}
                                >
                                    <span style={{ width: `${(currentPosition / audioDuration) * 100}%` }}></span>
                                </span>
                            }

                            { !shown.info &&
                                <span
                                    className="progress-bar"
                                >
                                    <span style={{ width: `${(currentPositionLive) * 100}%` }}></span>
                                </span>
                            }                                
                        </div>
                    </div>
                    <div className="col-2 d-block d-lg-none text-end"><span className="show-more"  onClick={switchActive}></span></div>
                    <div className="col-12 col-lg-6 col-xl-7 d-none d-lg-block footer-more active">
                        <div className="row align-items-center">
                            <div className="col-12 col-lg-12 col-xl-7"><div className="search-song-outer"><SearchSongs channel={liveChannel || 'rgw'} /></div></div>
                            <div className="col-12 col-xl-5 text-end d-none d-xl-block buttons">
                                <div className="btn-main-sm"><Link to='/musikwunsch'>Musikwunsch</Link></div>
                                <div className="btn-main-sm right"><Link to='/kontakt'>Kontakt</Link></div>
                            </div>
                        </div>
    
                                           
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AudioPlayer;
